/**
 * Survival — hearts, hunger, thirst, sleep, and death.
 *
 * Kate's numbers, verbatim where she gave them:
 *   Hearts:  "every player gets 25 hearts... So 2 heart rows. So actually 24, 24."
 *   Hunger:  "one level of your hunger bar runs out every 5 minutes... especially if
 *            you're sprinting, like if you're running really fast, um, then your hunger
 *            bar depletes, uh, 2 minutes and 30 seconds."
 *   Dying:   "You have 5 minutes before you die when your hunger bar runs out."
 *   Thirst:  "you also have to drink in this game" — rate not specified (see
 *            Questions for Kate).
 *   Water:   "if you jump in water, you can wash yourself off."
 *   Sleep:   "you can sleep. But you don't have to sleep."
 */

/** Kate said 25, then corrected herself to 24 — two rows of twelve. */
export const MAX_HEARTS = 24
export const HEART_ROWS = 2

/** Levels in the hunger and thirst bars. */
export const MAX_HUNGER = 10
export const MAX_THIRST = 10

/** Seconds per hunger level, walking and sprinting. Both are Kate's figures. */
const HUNGER_SECONDS_PER_LEVEL = 5 * 60
const HUNGER_SECONDS_PER_LEVEL_SPRINTING = 150

/**
 * Thirst drains at the same rate as walking hunger. Kate specified that you must drink
 * but never gave a rate, so this mirrors the one number she did give rather than
 * inventing a second pace. Flagged in Questions for Kate.
 */
const THIRST_SECONDS_PER_LEVEL = 5 * 60

/** "You have 5 minutes before you die when your hunger bar runs out." */
const STARVING_SECONDS = 5 * 60
/** Thirst mirrors it, for the same reason as the drain rate. */
const PARCHED_SECONDS = 5 * 60

/** Hearts lost per second once the grace period expires. */
const COLLAPSE_HEARTS_PER_SECOND = 2

export type DeathCause = 'hunger' | 'thirst' | 'wounds' | null

export class Survival {
  hearts = MAX_HEARTS

  /**
   * Bars are stored as continuous floats and rounded up for display, rather than
   * accumulating a carry and decrementing on each whole unit. Summing dt/rate 9,000
   * times lands a hair under 1.0, so a carry-based loop silently skipped ticks —
   * sprinting drained 3 levels in 10 minutes where Kate's numbers demand 4.
   */
  private hungerExact = MAX_HUNGER
  private thirstExact = MAX_THIRST

  get hunger(): number {
    return Math.max(0, Math.ceil(this.hungerExact - 1e-9))
  }

  get thirst(): number {
    return Math.max(0, Math.ceil(this.thirstExact - 1e-9))
  }
  /** Seconds left to eat once the hunger bar hits zero. Null while not starving. */
  starvingFor: number | null = null
  parchedFor: number | null = null
  /** True while asleep. Kate made this optional, never forced. */
  asleep = false
  dead = false
  deathCause: DeathCause = null
  /** Counts up while dirty; washing in water clears it. */
  dirty = 0

  /** Advance one fixed step. `sprinting` doubles the hunger rate, as Kate specified. */
  update(dt: number, sprinting: boolean, inWater: boolean): void {
    if (this.dead) return

    // Sleeping pauses the drain — you are not burning energy walking around.
    if (!this.asleep) {
      const secondsPerLevel = sprinting
        ? HUNGER_SECONDS_PER_LEVEL_SPRINTING
        : HUNGER_SECONDS_PER_LEVEL
      this.hungerExact = Math.max(0, this.hungerExact - dt / secondsPerLevel)
      this.thirstExact = Math.max(0, this.thirstExact - dt / THIRST_SECONDS_PER_LEVEL)
      this.dirty = Math.min(1, this.dirty + dt / 240)
    }

    // Kate: "if you jump in water, you can wash yourself off." Water also lets you drink.
    if (inWater) {
      this.dirty = 0
      this.drink()
    }

    this.starvingFor = this.tickGrace(this.hunger, this.starvingFor, STARVING_SECONDS, dt, 'hunger')
    this.parchedFor = this.tickGrace(this.thirst, this.parchedFor, PARCHED_SECONDS, dt, 'thirst')
  }

  /**
   * Run the grace countdown for an empty bar. Returns the remaining seconds, or null
   * when the bar isn't empty. Once it expires the player collapses.
   */
  private tickGrace(
    level: number,
    remaining: number | null,
    graceSeconds: number,
    dt: number,
    cause: 'hunger' | 'thirst',
  ): number | null {
    if (level > 0) return null
    const next = (remaining ?? graceSeconds) - dt
    if (next > 0) return next
    this.damage(COLLAPSE_HEARTS_PER_SECOND * dt, cause)
    return 0
  }

  /** Take damage from any source. */
  damage(amount: number, cause: DeathCause = 'wounds'): void {
    if (this.dead) return
    this.hearts = Math.max(0, this.hearts - amount)
    if (this.hearts <= 0) {
      this.dead = true
      this.deathCause = cause
    }
  }

  eat(levels = MAX_HUNGER): void {
    this.hungerExact = Math.min(MAX_HUNGER, this.hungerExact + levels)
    if (this.hunger > 0) this.starvingFor = null
  }

  drink(levels = MAX_THIRST): void {
    this.thirstExact = Math.min(MAX_THIRST, this.thirstExact + levels)
    if (this.thirst > 0) this.parchedFor = null
  }

  /** Sleep is optional and only at night — Kate: "when it gets night, you can sleep." */
  /**
   * Kate, 2026-08-24, listing it out herself: "you don't always need to burrow to
   * sleep. You only need to burrow to sleep when you're a rabbit, a wolf, a bear, a
   * cat, a deer, a dog, and a chameleon... With humans you can just sleep. But with
   * birds, you just need to build a nest."
   *
   * @param shelter what the animal is currently inside, if anything
   * @param needs   what this species has to be inside to sleep
   */
  trySleep(isNight: boolean, shelter: 'burrow' | 'nest' | null,
           needs: 'burrow' | 'nest' | 'nothing'): boolean {
    if (!isNight || this.dead) return false
    if (needs !== 'nothing' && shelter !== needs) return false
    this.asleep = true
    return true
  }

  /**
   * Kate: "if I'm just taking damage... I need to be able to heal." Sleeping a full
   * night is the obvious way to get hearts back, so a night's sleep mends you.
   */
  heal(amount: number): void {
    if (this.dead) return
    this.hearts = Math.min(MAX_HEARTS, this.hearts + amount)
  }

  wake(): void {
    this.asleep = false
  }

  /** Fresh start after respawning. The 50-coupon cost is charged by the economy (M5). */
  respawn(): void {
    this.hearts = MAX_HEARTS
    this.hungerExact = MAX_HUNGER
    this.thirstExact = MAX_THIRST
    this.starvingFor = null
    this.parchedFor = null
    this.asleep = false
    this.dead = false
    this.deathCause = null
    this.dirty = 0
  }
}
